import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { ButtonSpinner } from '../../components/ui/Loader';
import {
  useGetSyriatelCashPaymentsQuery,
  useAcceptSyriatelCashPaymentMutation,
  useRejectSyriatelCashPaymentMutation,
} from '../../store/api/adminApi';

export function AdminSyriatelCashPaymentsPage() {
  const { t } = useTranslation();
  const [statusFilter, setStatusFilter] = useState('');
  const [page, setPage] = useState(1);

  const { data, isLoading, refetch } = useGetSyriatelCashPaymentsQuery({
    status: statusFilter || undefined,
    page,
    limit: 20,
  });

  const [acceptPayment, { isLoading: isAccepting }] = useAcceptSyriatelCashPaymentMutation();
  const [rejectPayment, { isLoading: isRejecting }] = useRejectSyriatelCashPaymentMutation();

  const payments = data?.data?.payments || [];
  const pagination = data?.data?.pagination || { page: 1, limit: 20, total: 0, pages: 1 };

  const handleAccept = async (id: string) => {
    try {
      await acceptPayment({ id }).unwrap();
      toast.success(t('admin.syriatelCashPayments.acceptSuccess'));
      void refetch();
    } catch {
      toast.error(t('admin.syriatelCashPayments.acceptError'));
    }
  };

  const handleReject = async (id: string) => {
    try {
      await rejectPayment({ id }).unwrap();
      toast.success(t('admin.syriatelCashPayments.rejectSuccess'));
      void refetch();
    } catch {
      toast.error(t('admin.syriatelCashPayments.rejectError'));
    }
  };

  const statusBadge = (status: string) => {
    const colors: Record<string, string> = {
      pending: 'bg-yellow-500/20 text-yellow-500',
      accepted: 'bg-green-500/20 text-green-400',
      rejected: 'bg-danger/20 text-danger',
    };
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-body ${colors[status] || 'bg-neutral-dark text-neutral-medium'}`}>
        {t(`admin.syriatelCashPayments.status.${status}`)}
      </span>
    );
  };

  return (
    <div className="space-y-6 p-6">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-heading font-bold text-brand-accent">
            {t('admin.syriatelCashPayments.title')}
          </h1>
          <p className="text-neutral-medium font-body mt-1">
            {t('admin.syriatelCashPayments.subtitle')}
          </p>
        </div>
      </div>

      <div className="card-glass rounded-2xl shadow-2xl p-6">
        <div className="flex flex-wrap gap-4 mb-6">
          <div>
            <label className="block text-sm font-medium text-neutral-medium mb-2 font-body">
              {t('admin.syriatelCashPayments.filters.status')}
            </label>
            <select
              value={statusFilter}
              onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}
              className="px-4 py-2 bg-neutral-dark border border-neutral-dark rounded-lg text-neutral-light font-body focus:outline-none focus:border-brand-accent"
            >
              <option value="">{t('admin.transactions.filters.all')}</option>
              <option value="pending">{t('admin.syriatelCashPayments.status.pending')}</option>
              <option value="accepted">{t('admin.syriatelCashPayments.status.accepted')}</option>
              <option value="rejected">{t('admin.syriatelCashPayments.status.rejected')}</option>
            </select>
          </div>
        </div>

        {isLoading ? (
          <div className="flex items-center justify-center py-16">
            <ButtonSpinner />
            <span className="ml-2 text-neutral-medium">{t('common.loading')}</span>
          </div>
        ) : payments.length === 0 ? (
          <div className="text-center py-16">
            <p className="text-neutral-medium text-xl font-body">{t('admin.syriatelCashPayments.empty')}</p>
          </div>
        ) : (
          <>
            <div className="hidden md:block overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-neutral-dark">
                    <th className="text-left py-3 px-4 text-sm font-heading font-semibold text-neutral-medium">{t('admin.syriatelCashPayments.table.user')}</th>
                    <th className="text-left py-3 px-4 text-sm font-heading font-semibold text-neutral-medium">{t('admin.syriatelCashPayments.table.amount')}</th>
                    <th className="text-left py-3 px-4 text-sm font-heading font-semibold text-neutral-medium">{t('admin.syriatelCashPayments.table.transactionId')}</th>
                    <th className="text-left py-3 px-4 text-sm font-heading font-semibold text-neutral-medium">{t('admin.syriatelCashPayments.table.source')}</th>
                    <th className="text-left py-3 px-4 text-sm font-heading font-semibold text-neutral-medium">{t('admin.syriatelCashPayments.table.status')}</th>
                    <th className="text-left py-3 px-4 text-sm font-heading font-semibold text-neutral-medium">{t('admin.syriatelCashPayments.table.date')}</th>
                    <th className="text-left py-3 px-4 text-sm font-heading font-semibold text-neutral-medium">{t('admin.syriatelCashPayments.table.actions')}</th>
                  </tr>
                </thead>
                <tbody>
                  {payments.map((payment: any) => (
                    <tr key={payment._id} className="border-b border-neutral-dark/50 hover:bg-neutral-dark/20 transition-colors">
                      <td className="py-3 px-4">
                        <div>
                          <span className="text-neutral-light text-sm font-body">
                            {payment.user?.firstName || 'N/A'}
                          </span>
                          {payment.user?.telegramId && (
                            <span className="text-neutral-medium text-xs font-body block">
                              ID: {payment.user.telegramId}
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="py-3 px-4">
                        <span className="text-neutral-light font-body text-sm">
                          {payment.amount.toFixed(2)} SYP
                        </span>
                      </td>
                      <td className="py-3 px-4">
                        <span className="text-neutral-light font-mono text-xs">
                          {payment.transactionId}
                        </span>
                      </td>
                      <td className="py-3 px-4">
                        <span className="text-neutral-light text-sm font-body">
                          {payment.source === 'telegram' ? 'Telegram' : 'Website'}
                        </span>
                      </td>
                      <td className="py-3 px-4">{statusBadge(payment.status)}</td>
                      <td className="py-3 px-4">
                        <span className="text-neutral-medium text-sm font-body">
                          {new Date(payment.createdAt).toLocaleDateString()}
                        </span>
                      </td>
                      <td className="py-3 px-4">
                        {payment.status === 'pending' && (
                          <div className="flex gap-2">
                            <button
                              onClick={() => void handleAccept(payment._id)}
                              disabled={isAccepting || isRejecting}
                              className="px-3 py-1.5 bg-green-500/20 text-green-400 rounded-lg hover:bg-green-500/30 transition-colors text-xs font-heading font-semibold disabled:opacity-50"
                            >
                              {t('admin.syriatelCashPayments.accept')}
                            </button>
                            <button
                              onClick={() => void handleReject(payment._id)}
                              disabled={isAccepting || isRejecting}
                              className="px-3 py-1.5 bg-danger/20 text-danger rounded-lg hover:bg-danger/30 transition-colors text-xs font-heading font-semibold disabled:opacity-50"
                            >
                              {t('admin.syriatelCashPayments.reject')}
                            </button>
                          </div>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="md:hidden space-y-4">
              {payments.map((payment: any) => (
                <div key={payment._id} className="bg-neutral-dark/30 rounded-lg p-4 space-y-2">
                  <div className="flex justify-between items-start">
                    <div>
                      <span className="text-neutral-light font-body text-sm">{payment.user?.firstName || 'N/A'}</span>
                      {payment.user?.telegramId && (
                        <span className="text-neutral-medium text-xs font-body block">ID: {payment.user.telegramId}</span>
                      )}
                    </div>
                    {statusBadge(payment.status)}
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-neutral-medium font-body">{t('admin.syriatelCashPayments.table.amount')}:</span>
                    <span className="text-neutral-light font-body">{payment.amount.toFixed(2)} SYP</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-neutral-medium font-body">{t('admin.syriatelCashPayments.table.transactionId')}:</span>
                    <span className="text-neutral-light font-mono text-xs">{payment.transactionId}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-neutral-medium font-body">{t('admin.syriatelCashPayments.table.source')}:</span>
                    <span className="text-neutral-light font-body">{payment.source === 'telegram' ? 'Telegram' : 'Website'}</span>
                  </div>
                  {payment.status === 'pending' && (
                    <div className="flex gap-2 pt-2">
                      <button
                        onClick={() => void handleAccept(payment._id)}
                        disabled={isAccepting || isRejecting}
                        className="flex-1 px-3 py-2 bg-green-500/20 text-green-400 rounded-lg hover:bg-green-500/30 transition-colors text-sm font-heading font-semibold disabled:opacity-50"
                      >
                        {t('admin.syriatelCashPayments.accept')}
                      </button>
                      <button
                        onClick={() => void handleReject(payment._id)}
                        disabled={isAccepting || isRejecting}
                        className="flex-1 px-3 py-2 bg-danger/20 text-danger rounded-lg hover:bg-danger/30 transition-colors text-sm font-heading font-semibold disabled:opacity-50"
                      >
                        {t('admin.syriatelCashPayments.reject')}
                      </button>
                    </div>
                  )}
                </div>
              ))}
            </div>

            {pagination.pages > 1 && (
              <div className="flex items-center justify-center gap-2 mt-6">
                <button
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page <= 1}
                  className="px-3 py-1.5 bg-neutral-dark text-neutral-light rounded-lg hover:bg-neutral-dark/80 transition-colors text-sm disabled:opacity-50"
                >
                  {t('common.previous')}
                </button>
                <span className="text-neutral-medium text-sm font-body">
                  {page} / {pagination.pages}
                </span>
                <button
                  onClick={() => setPage((p) => Math.min(pagination.pages, p + 1))}
                  disabled={page >= pagination.pages}
                  className="px-3 py-1.5 bg-neutral-dark text-neutral-light rounded-lg hover:bg-neutral-dark/80 transition-colors text-sm disabled:opacity-50"
                >
                  {t('common.next')}
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
